import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Container, Typography, Paper, Box, Fab, Grid, alpha, Alert,
    Stack // Import Stack for easy horizontal layout
} from '@mui/material';
import { AdminPanelSettings, Email } from '@mui/icons-material'; // Import Email icon
import { format } from 'date-fns';

// Theme
import { ThemeProvider } from '@mui/material';
import theme from './theme/theme';

// Components
import BlinkingTeeBot from './components/BlinkingTeebot'; // Adjust path if needed
import CourseSelection from './components/CourseSelection';
import SearchParameters from './components/SearchParameters';
import SearchResults from './components/SearchResults';
import PasswordProtectedAdmin from './components/AdminPasswordModal';
import ContactUsModal from './components/ContactUsModal'; // Import the new modal

// Utils
import { useDebounce } from './hooks/useDebounce';

// Constants for polling
const POLLING_INITIAL_INTERVAL = 2000; // Start with 2 seconds
const POLLING_MAX_INTERVAL = 10000; // Cap at 10 seconds
const POLLING_BACKOFF_FACTOR = 1.5; // Grow by 50% each time
const POLLING_MAX_DURATION = 5 * 60 * 1000; // 5 minutes max polling time
const POLLING_MAX_ATTEMPTS = 100; // Safety limit

function App() {
    // State management
    const [courseOptions, setCourseOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [date, setDate] = useState(new Date());
    const [startHour, setStartHour] = useState('07:00');
    const [endHour, setEndHour] = useState('17:00');
    const [players, setPlayers] = useState(2);
    const [searchId, setSearchId] = useState(null);
    const [results, setResults] = useState({});
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [courseAddedAlert, setCourseAddedAlert] = useState(null);
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false); // New state for Contact Us modal

    // Refs for managing polling
    const activeSearchRef = useRef(null);
    const pollingTimeoutRef = useRef(null);
    const pollingStartTimeRef = useRef(null);
    const pollingAttemptsRef = useRef(0);
    const abortControllerRef = useRef(null);

    // Logger function with production toggle
    const isProduction = process.env.NODE_ENV === 'production';
    const logger = useCallback((message, level = 'info', data = null) => {
        if (isProduction && level === 'debug') return; // Skip debug logs in production

        const timestamp = new Date().toISOString();
        const searchIdPrefix = activeSearchRef.current ? `[${activeSearchRef.current}] ` : '';

        switch (level) {
            case 'error':
                console.error(`${timestamp} ${searchIdPrefix}${message}`, data || '');
                break;
            case 'warn':
                console.warn(`${timestamp} ${searchIdPrefix}${message}`, data || '');
                break;
            case 'debug':
                console.debug(`${timestamp} ${searchIdPrefix}${message}`, data || '');
                break;
            case 'info':
            default:
                console.log(`${timestamp} ${searchIdPrefix}${message}`, data || '');
        }
    }, [isProduction]);

    // Fetch course suggestions with debounce
    const fetchCourseOptionsRaw = useCallback(async (query) => {
        if (query.length < 2) {
            setCourseOptions([]);
            return;
        }
        setLoadingOptions(true);
        try {
            const response = await axios.get(`/api/courses?q=${query}`);
            setCourseOptions(response.data || []);
        } catch (error) {
            logger('Error fetching course options:', 'error', error);
            setCourseOptions([]);
        } finally {
            setLoadingOptions(false);
        }
    }, [logger]);

    const fetchCourseOptions = useDebounce(fetchCourseOptionsRaw, 300);

    useEffect(() => {
        fetchCourseOptions(searchTerm);
    }, [searchTerm, fetchCourseOptions]);

    // Update end hour when start hour changes
    useEffect(() => {
        if (startHour) {
            const startHourInt = parseInt(startHour.split(':')[0], 10);
            const endHourInt = parseInt(endHour.split(':')[0], 10);

            if (endHourInt <= startHourInt) {
                const newEndHourValue = Math.min(startHourInt + 1, 18);
                const newEndHour = newEndHourValue.toString().padStart(2, '0') + ':00';
                setEndHour(newEndHour);
            }
        }
    }, [startHour, endHour]);

    // Stop all polling
    const stopPolling = useCallback(() => {
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }

        // Cancel any in-flight requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Reset counters
        pollingAttemptsRef.current = 0;
        pollingStartTimeRef.current = null;
    }, []); // stopPolling doesn't depend on logger or anything else state/prop specific here

    // Improved polling logic with refs and better error handling
    const pollResults = useCallback(async (newSearchId) => {

        // Stop any existing polling
        stopPolling();

        // Update state and refs
        setSearchId(newSearchId);
        activeSearchRef.current = newSearchId;
        pollingStartTimeRef.current = Date.now();
        pollingAttemptsRef.current = 0;
        setResults({}); // Clear previous results

        // Function to perform the actual polling with proper error handling
        const executePoll = async () => {
            // Check if this is still the active search
            if (activeSearchRef.current !== newSearchId) {
                logger(`Poll stopped: Search ID mismatch (active: ${activeSearchRef.current}, current: ${newSearchId})`, 'debug');
                return;
            }

            // Check if we've reached the maximum number of attempts
            if (pollingAttemptsRef.current >= POLLING_MAX_ATTEMPTS) {
                setSearchError('Search is taking longer than expected. Please try again or contact support if this persists.');
                logger(`Poll stopped: Max attempts reached (${POLLING_MAX_ATTEMPTS}) for search ID ${newSearchId}`, 'warn');
                setLoadingSearch(false);
                stopPolling();
                return;
            }

            // Check if we've exceeded the maximum polling time
            const elapsedTime = Date.now() - pollingStartTimeRef.current;
            if (elapsedTime > POLLING_MAX_DURATION) {
                setSearchError('Search timed out. Please try again or contact support if this persists.');
                logger(`Poll stopped: Max duration reached (${POLLING_MAX_DURATION}ms) for search ID ${newSearchId}`, 'warn');
                setLoadingSearch(false);
                stopPolling();
                return;
            }

            // Increment attempt counter
            pollingAttemptsRef.current++;

            // Calculate next interval using exponential backoff
            const currentAttempt = pollingAttemptsRef.current;
             let nextInterval = POLLING_INITIAL_INTERVAL;

             // Apply backoff after the first few attempts
             if (currentAttempt > 5) { // Start backoff after attempt 5
                 nextInterval = Math.min(
                   POLLING_INITIAL_INTERVAL * Math.pow(POLLING_BACKOFF_FACTOR, currentAttempt - 5),
                   POLLING_MAX_INTERVAL
                 );
             }
             // Add some jitter (optional but good practice)
             nextInterval = nextInterval * (0.9 + Math.random() * 0.2); // +/- 10% jitter


            logger(`Polling search ${newSearchId} (attempt ${currentAttempt}, next in ${nextInterval.toFixed(0)}ms)`, 'debug');

            try {
                // Create a new AbortController for this request
                abortControllerRef.current = new AbortController();

                // Make the request with the signal
                const response = await axios.get(
                    `/api/search/${newSearchId}/results`,
                    { signal: abortControllerRef.current.signal }
                );

                const data = response.data;
                logger(`Received poll results for ${newSearchId}:`, 'debug', data);


                // Handle results data - API returns { results: [{course_id, tee_times, ...}, ...], completed: boolean }
                // Convert the results array into an object keyed by course_id
                if (data && Array.isArray(data.results)) {
                     // Transform array into object keyed by course_id
                     const resultsByCourseId = {};
                     data.results.forEach(courseResult => {
                         if (courseResult && courseResult.course_id != null) {
                             resultsByCourseId[courseResult.course_id] = courseResult;
                         }
                     });

                     setResults(prevResults => {
                        const newResults = { ...prevResults };

                        // Merge in new results for each course ID
                        Object.keys(resultsByCourseId).forEach(courseId => {
                            newResults[courseId] = resultsByCourseId[courseId];
                        });

                        return newResults;
                     });
                } else if (data && data.results !== undefined) {
                     logger('Warning: Received results in unexpected format:', 'warn', data.results);
                }


                // Check if search is complete
                if (data.completed) {
                    logger(`Search ${newSearchId} completed.`, 'info');
                    setLoadingSearch(false);
                    stopPolling();
                    return; // Exit the function, do not schedule next poll
                }

                // Schedule next poll only if this is still the active search
                if (activeSearchRef.current === newSearchId) {
                    pollingTimeoutRef.current = setTimeout(executePoll, nextInterval);
                } else {
                     logger(`Poll loop ending: Search ID changed while waiting for timeout (${activeSearchRef.current} vs ${newSearchId})`, 'debug');
                }

            } catch (error) {
                // Check if request was aborted intentionally
                if (error.name === 'AbortError' || error.name === 'CanceledError') {
                     logger(`Poll request aborted for search ID ${newSearchId}`, 'debug');
                     return; // Don't handle as an error, it was intentional
                }

                // Log the error
                logger('Error polling search results:', 'error', error);

                // Handle network/server errors
                if (error.response) {
                    logger('Error response details:', 'error', {
                        status: error.response.status,
                        data: error.response.data
                    });

                    // Handle specific error codes
                    if (error.response.status === 404) {
                        setSearchError(`Search ${newSearchId} not found. It may have been removed or expired.`);
                    } else if (error.response.status >= 500) {
                        setSearchError('Server error occurred while fetching results. Please try again in a few minutes.');
                    } else {
                         // Use a more generic message for other client-side errors or unexpected responses
                        setSearchError(`Error fetching results: ${error.response.data?.detail || `Server returned status ${error.response.status}`}`);
                    }
                } else if (error.request) {
                    logger('No response received details:', 'error', error.request);
                    setSearchError('Network error while fetching results. Please check your connection and try again.');
                } else {
                    logger('Error details:', 'error', error.message);
                    setSearchError(`An unexpected error occurred: ${error.message}`);
                }

                // Stop loading and polling on error, but only if this is still the active search
                if (activeSearchRef.current === newSearchId) {
                    setLoadingSearch(false);
                    stopPolling();
                } else {
                     logger(`Error occurred for old search ID ${newSearchId}. Current active is ${activeSearchRef.current}. Ignoring error display for old search.`, 'debug');
                }
            }
        };

        // Start the first polling attempt after a small initial delay
        pollingTimeoutRef.current = setTimeout(executePoll, POLLING_INITIAL_INTERVAL);

    }, [stopPolling, logger]); // Included logger in dependencies

    // Cleanup on unmount or when stopPolling changes (though stopPolling is stable due to useCallback)
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]); // Dependency on stopPolling is good practice

    // Handle search submission
    const handleSearch = async () => {
        if (selectedCourses.length === 0) {
            setSearchError('Please select at least one course.');
            return;
        }

        setLoadingSearch(true);
        setSearchError('');
        setResults({}); // Clear previous results immediately
        stopPolling(); // Stop any previous polling

        try {
            const payload = {
                course_ids: selectedCourses.map(c => c.id),
                date: format(date, 'yyyy-MM-dd'),
                start_time: startHour,
                end_time: endHour,
                players: players,
            };

            logger('Initiating new search:', 'info', payload);
            const response = await axios.post(`/api/search`, payload);
            const newSearchId = response.data.search_id;
            logger(`Search initiated successfully. Search ID: ${newSearchId}`, 'info');

            // Start polling with the new search ID
            pollResults(newSearchId);

        } catch (error) {
            logger('Search initiation error:', 'error', error);

            // Handle initiation errors
            if (error.response) {
                setSearchError(`Search initiation failed: ${error.response.data?.detail || `Server returned ${error.response.status}`}`);
            } else if (error.request) {
                setSearchError('Network error while initiating search. Please check your connection and try again.');
            } else {
                setSearchError(`An unexpected error occurred: ${error.message}`);
            }

            setLoadingSearch(false);
            setSearchId(null); // Ensure searchId is null if initiation fails
        }
    };

    const handleAddCourse = (event, value) => {
        if (value && !selectedCourses.some(c => c.id === value.id)) {
            // Add the course to selected courses
            setSelectedCourses(prev => [...prev, value]);

            // Set the course added alert (will trigger the overlay notification)
            setCourseAddedAlert(value);

            // Clear the alert after a delay
            setTimeout(() => {
                setCourseAddedAlert(null);
            }, 2000); // Alert duration in ms
        }

        setSearchTerm(''); // Clear search term after adding
        setCourseOptions([]); // Clear options after adding
    };

    // Handle removing a course chip
    const handleRemoveCourse = (courseToRemove) => {
        setSelectedCourses(prev => prev.filter(c => c.id !== courseToRemove.id));
        // If the removed course had results, clear them
        setResults(prevResults => {
             const newResults = { ...prevResults };
             delete newResults[courseToRemove.id];
             return newResults;
        });
    };

    // Handlers for Contact Us Modal
    const handleOpenContactModal = () => setIsContactModalOpen(true);
    const handleCloseContactModal = () => setIsContactModalOpen(false);


    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    backgroundImage: `url(/teebot_background.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                    padding: { xs: 2, md: 4 },
                }}
            >
                <Container maxWidth="lg">
                    {/* Buttons Container (Admin & Contact Us) */}
                    {/* Using Stack to arrange buttons horizontally */}
                    <Stack
                        direction="row"
                        spacing={1} // Adds spacing between buttons
                        sx={{
                            position: 'fixed',
                            top: '16px', // Position from the top
                            right: '16px', // Position from the right
                            zIndex: 1100, // Ensure they are above other content
                        }}
                    >
                        {/* Admin Button */}
                        {/* Removed fixed positioning styles from the Fab itself */}
                        <Fab
                            variant="extended"
                            size="small"
                            onClick={() => setIsAdminModalOpen(true)}
                            aria-label="manage courses"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                                // Explicit height/fontSize might make text too small with extended variant
                                // Let the size="small" prop handle Fab sizing defaults
                                // height: '16px',
                                // fontSize: '0.6rem',
                            }}
                        >
                            <AdminPanelSettings sx={{ mr: 1 /*, fontSize: '16px'*/ }} /> {/* Adjust icon size if needed */}
                            Manage Courses
                        </Fab>

                        {/* Contact Us Button - NEW */}
                        <Fab
                            variant="extended"
                            size="small"
                            onClick={handleOpenContactModal} // Open the contact modal
                            aria-label="contact us"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.55)', // Consistent styling
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                                // Let the size="small" prop handle Fab sizing defaults
                            }}
                        >
                            <Email sx={{ mr: 1 /*, fontSize: '16px'*/ }} /> {/* Email icon */}
                            Contact Us
                        </Fab>
                    </Stack>

                    {/* Header Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            mt: { xs: 5, sm: 0 }
                        }}
                    >
                        <BlinkingTeeBot
                            sx={{ // Pass the original sx styles as a prop
                                maxWidth: {
                                    xs: '90px',
                                    sm: '120px',
                                    md: '150px',
                                    lg: '200px',
                                },
                                height: 'auto',
                                mr: '12px',
                                mb: {
                                    xs: '-60px',
                                    sm: '-90px',
                                    md: '-110px',
                                    lg: '-140px'
                                },
                            }}
                        />
                        <Box
                            component="img"
                            src="/teebot_logo.png"
                            alt="Teebot Text"
                            sx={{
                                maxWidth: {
                                    xs: '200px',
                                    sm: '200px',
                                    md: '300px',
                                    lg: '400px',
                                },
                                height: 'auto',
                                mr: '12px',
                                mb: {
                                    xs: '-20px',
                                    sm: '-30px',
                                    md: '-35px',
                                    lg: '-40px',
                                },
                            }}
                        />
                        <Typography
                            variant="body2"
                            component="span"
                            sx={(theme) => ({ // Use callback syntax to access theme if needed
                                // Original style for responsiveness
                                display: { xs: 'none', sm: 'inline' },

                                // --- Added styles ---
                                color: theme.palette.common.white, // Set text color to white using theme palette
                                backgroundColor: alpha(theme.palette.primary.dark, 0.2), // Dark background with some opacity
                                borderRadius: theme.shape.borderRadius, // Use theme's border radius
                                padding: theme.spacing(0.5, 2), // Add some padding
                                marginLeft: theme.spacing(2), // Adjust margin to be relative to the logo
                                // Ensure sufficient margin-bottom if the logo has negative margin
                                marginBottom: theme.spacing(1),
                                textShadow: '1px 1px 2px rgba(0,0,0,0.5)', // Optional: add text shadow for readability

                            })}
                        >
                            AI Powered Tee Time Search Engine
                        </Typography>
                    </Box>

                    {/* Main Search Form Paper */}
                    <Paper
                        elevation={4}
                        sx={{
                            p: { xs: 2, md: 3 },
                            background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.light, 0.25)}, ${alpha(theme.palette.primary.light, 0.0)})`,
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            borderRadius: 4,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        }}
                    >
                        <Grid container spacing={2} alignItems="flex-end" direction="column">
                            {/* Course Selection */}
                            {/* Adjusted Grid item to take full width */}
                            <Grid item xs={12} sx={{ width: '100%' }}>
                                <CourseSelection
                                    courseOptions={courseOptions}
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    loadingOptions={loadingOptions}
                                    selectedCourses={selectedCourses}
                                    handleAddCourse={handleAddCourse}
                                    handleRemoveCourse={handleRemoveCourse}
                                    courseAddedAlert={courseAddedAlert}
                                />
                            </Grid>
                            {/* Search Parameters */}
                            {/* Adjusted Grid item to take full width */}
                            <Grid item xs={12} sx={{ width: '100%' }}>
                                <SearchParameters
                                    date={date}
                                    setDate={setDate}
                                    startHour={startHour}
                                    setStartHour={setStartHour}
                                    endHour={endHour}
                                    setEndHour={setEndHour}
                                    players={players}
                                    setPlayers={setPlayers}
                                    handleSearch={handleSearch}
                                    loadingSearch={loadingSearch}
                                    selectedCourses={selectedCourses}
                                />
                            </Grid>
                        </Grid>

                        {/* Search Error Message */}
                        {searchError && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: '0.9rem', p: '4px 12px' }}>
                                {searchError}
                            </Alert>
                        )}
                    </Paper>

                    {/* Results Area */}
                    {(loadingSearch || Object.keys(results).length > 0 || searchId) && (
                        <SearchResults
                            loadingSearch={loadingSearch}
                            results={results}
                            searchId={searchId}
                            selectedCourses={selectedCourses}
                        />
                    )}

                    {/* Admin Modal */}
                    <PasswordProtectedAdmin open={isAdminModalOpen} onClose={() => setIsAdminModalOpen(false)} />

                    {/* Contact Us Modal - NEW */}
                    <ContactUsModal
                      open={isContactModalOpen}
                      onClose={handleCloseContactModal}
                      BackdropProps={{
                        sx: {
                          opacity: 0.1, // Or your desired opacity
                        },
                      }}
                    />
                </Container>
            </Box>
        </ThemeProvider>
    );
}

export default App;